import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import { api, SERVER_ORIGIN } from '../services/api';
import { AlertCircle, Check, ChevronDown, Phone, Mail, Plus, ExternalLink, MessageCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { businessToday, isoPlusDays } from '../utils/time';
import AccountMark from './AccountMark';
import { useAuth } from '../services/AuthContext';
import { ApprovalRows, ApproveAllButton, useApprovalsQueue } from './ApprovalsQueue';

/**
 * The "needs attention" list from the V1 dashboard spec — new leads, route
 * problems, open equipment issues, and anything captured from outside the app,
 * ranked so the expensive things sit on top. Backed by GET /api/attention.
 */

// Two lines of detail is the rule — a long note must not push the next thing
// off his screen. The shop weekly is the one that shows whole: its detail says
// WHICH lines were left blank, and, when nobody has the Mechanic tag (owner,
// Oct 6 2026: the list is "a part of the mechanic employee tag"), where to
// tick it. Cut at two lines on a phone that read "…open the person → tick…"
// with the rest hidden, and tapping the row goes to the list, not to the rest
// of the sentence. Its line break is kept: the tag sentence, then the week.
const detailClass = (kind: string) =>
  `text-xs text-gray-500 mt-0.5 ${kind === 'shop_weekly' ? 'whitespace-pre-line' : 'line-clamp-2'}`;

interface AttentionItem {
  key: string;
  kind: 'offer' | 'lead' | 'job' | 'equipment' | 'maintenance' | 'schedule' | 'treatment'
      | 'unanswered' | 'interest' | 'care' | 'review' | 'expense' | 'inbound' | 'quote' | 'plan_request' | 'first_mow' | 'plan_buy' | 'autopay' | 'shop_weekly' | 'change_order' | 'online' | 'ask' | 'winter'
      | 'todo';   // a to-do for a client at an address, open and its day has come (services/todos)
  subkind?: string;
  customer_id?: number | null;
  account_mark?: 'P' | 'C' | null;
  ask_id?: number | null;
  title: string;
  detail: string | null;
  who: string | null;
  phone: string | null;
  email: string | null;
  url: string | null;
  // A Google review claim (services/reviewClaims.attentionItem): the card's two buttons, its screenshot and the review page.
  claim_id?: number | null;
  approve_label?: string | null;
  reject_label?: string | null;
  screenshot_url?: string | null;
  review_url?: string | null;
  // A reminder the crew's winter-upsell photos made (services/reminders): every picture, as signed /uploads links.
  photo_urls?: string[];
  href: string | null;
  action: string | null;
  why: string[];
  when: string | null;
  score: number;
}

interface AttentionGroup {
  key: string;
  label: string;
  items: AttentionItem[];
}

interface AttentionResponse {
  built_at: string;
  inbound_configured: boolean;
  counts: {
    total: number; lead: number; job: number; equipment: number;
    maintenance: number; schedule: number; inbound: number;
  };
  groups: AttentionGroup[];
  // Change requests waiting on the owner (services/changeRequests.pendingCount) — 0 for anyone else. The rows
  // themselves come from GET /change-requests through useApprovalsQueue; this is only so the header count is
  // right the moment the list lands.
  pending_approvals?: number;
}

const kindLabel: Record<string, string> = {
  offer: 'Mow ask',
  lead: 'New lead',
  job: 'Job',
  equipment: 'Repair',
  maintenance: 'Maintenance',
  schedule: 'Not booked',
  treatment: 'Treatment sold',
  quote: 'Bid accepted',
  bidday: 'Bid',
  change_order: 'Change order',   // on a bid they already accepted: waiting on them, or approved but not on the calendar yet
  online: 'Booked online',
  plan_request: 'Plan asked for',
  first_mow: 'Second mow',
  winter: 'Winter work',
  plan_buy: 'Friday shop',
  autopay: 'Card declined',
  unanswered: 'Waiting on you',
  ask: 'Texted to ask',
  interest: 'They wanted this',
  care: 'Unhappy customer',
  review: 'Google review',
  expense: 'Expense to sort',
  inbound: 'Inbox',
  shop_weekly: 'Shop weekly',
  todo: 'To-do',
};

const kindColor: Record<string, string> = {
  offer: 'bg-amber-100 text-amber-800',
  lead: 'bg-green-100 text-green-800',
  job: 'bg-blue-100 text-blue-800',
  equipment: 'bg-orange-100 text-orange-800',
  maintenance: 'bg-amber-100 text-amber-800',
  schedule: 'bg-rose-100 text-rose-800',
  treatment: 'bg-emerald-100 text-emerald-800',
  quote: 'bg-indigo-100 text-indigo-800',
  change_order: 'bg-indigo-100 text-indigo-800',
  online: 'bg-brand-100 text-brand-800',
  plan_request: 'bg-emerald-100 text-emerald-800',
  first_mow: 'bg-brand-100 text-brand-800',
  winter: 'bg-sky-100 text-sky-800',
  plan_buy: 'bg-brand-100 text-brand-800',
  autopay: 'bg-red-100 text-red-800',
  unanswered: 'bg-red-100 text-red-800',
  ask: 'bg-amber-100 text-amber-900',
  interest: 'bg-violet-100 text-violet-800',
  care: 'bg-red-100 text-red-800',
  review: 'bg-sky-100 text-sky-800',
  expense: 'bg-yellow-100 text-yellow-800',
  inbound: 'bg-brand-100 text-brand-800',
  shop_weekly: 'bg-amber-100 text-amber-800',
  todo: 'bg-slate-100 text-slate-700',
};

// Every item can be checked off. Leads / equipment / jobs / inbox do their real
// action server-side (a lead becomes contacted, a report resolved…); anything
// else — a waiting reply, an expense to sort, a review — is simply dismissed
// (hidden, reversible with Undo). See POST /attention/:key/done.

// Groups that stay open by default — the two you act on before the truck rolls.
const OPEN_BY_DEFAULT = ['now', 'reply'];

function ago(iso: string | null) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/**
 * `compact` is the dashboard mode: only the "Do first" group, with a link
 * through to the full page. The dashboard should say what needs doing before
 * the truck rolls, not carry a scrollable backlog — the day a biweekly off-week
 * put twenty-one identical rows on it made that obvious.
 */
export default function NeedsAttention({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  // "Waiting on your OK" — the owner's approval queue, first on the list (owner, Nov 2 2026: "approves and needs
  // attention together"). The same gate the queue's server route uses (requireOwner → role 'admin'): a manager or
  // the viewer never asks for it and never sees the block.
  const { user } = useAuth();
  const isOwner = user?.role === 'admin';
  const approvals = useApprovalsQueue(isOwner);
  // A complaint the crew owes a go-back for: schedules a 'fix' job (no charge,
  // no crew pay) on the customer's property and leaves the report open.
  const fixJob = useMutation(
    ({ id, date }: { id: number; date: string }) => api.post(`/care/${id}/fix-job`, { date }),
    {
      onSuccess: (r: any) => { toast.success(r.data?.message || 'Fix scheduled'); qc.invalidateQueries('attention'); qc.invalidateQueries('calendar-jobs'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not schedule the fix'); },
    }
  );
  function sendCrewBack(key: string) {
    const id = Number(String(key).split(':')[1]);
    // Austin's tomorrow, not UTC's — after 7pm the UTC spelling was two days out.
    const tomorrow = isoPlusDays(businessToday(), 1);
    const date = window.prompt('Send the crew back to fix it — which day? (YYYY-MM-DD)', tomorrow);
    if (!id || !date) return;
    fixJob.mutate({ id, date: date.trim() });
  }
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [newTodo, setNewTodo] = useState('');

  const { data, isLoading, isError } = useQuery<AttentionResponse>(
    'attention',
    () => api.get('/attention').then(r => r.data)
  );

  // Arriving from a Texts bubble with #service:12 (a request typed on the customer's page, server/services/texts
  // PORTAL_TYPE): that row is lit and scrolled into view once the list is here.
  const { hash } = useLocation();
  const wanted = hash ? decodeURIComponent(hash.slice(1)) : '';
  useEffect(() => {
    if (!wanted || !data) return;
    const el = document.getElementById(wanted);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [wanted, data]);

  function refreshAll() {
    qc.invalidateQueries('attention');
    qc.invalidateQueries('equipment-reports-open');
    qc.invalidateQueries(['jobs', 'today']);
  }

  // A bill the card on file would not pay: try the card again (services/autopay).
  const chargeAgain = useMutation(
    (invoiceId: number) => api.post(`/billing/invoices/${invoiceId}/charge-card`).then(r => r.data),
    {
      onSuccess: (d: any) => { toast.success(d?.message || 'Paid'); qc.invalidateQueries('attention'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'The card did not pay'); qc.invalidateQueries('attention'); },
    });
  // "Skip" on an as-needed ask (services/serviceMode): nothing is booked. The tick is "Put them on".
  const skipOffer = useMutation(
    (id: number) => api.post(`/as-needed/offers/${id}/decline`).then(r => r.data),
    {
      onSuccess: (d: any) => { toast.success(d?.message || 'Skipped'); qc.invalidateQueries('attention'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not skip that'); qc.invalidateQueries('attention'); },
    },
  );
  // A Google review claim: the two buttons the Billing card had, on the same routes (services/reviewClaims).
  // The server's own words come back as the toast; the item leaves the list because the claim's status changed.
  const decideClaim = useMutation(
    (v: { id: number; action: 'approve' | 'reject'; note?: string }) =>
      api.post(`/review-claims/${v.id}/${v.action}`, v.note ? { note: v.note } : {}).then(r => r.data),
    {
      onSuccess: (d: any) => {
        toast.success(d?.message || 'Done');
        qc.invalidateQueries('attention'); qc.invalidateQueries('my-day-attention'); qc.invalidateQueries('invoices');
        qc.invalidateQueries('billing'); qc.invalidateQueries('billing-review'); qc.invalidateQueries('review-claims');
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || (e?.response ? `The server answered ${e.response.status}` : 'No answer from the server — check the connection and try again'));
        qc.invalidateQueries('attention');
      },
    });
  function turnDownClaim(id: number) {
    const note = window.prompt('Turn it down — a word on why? (optional)', '');
    if (note === null) return;
    decideClaim.mutate({ id, action: 'reject', note: note.trim() || undefined });
  }
  const markDone = useMutation(
    (key: string) => api.post(`/attention/${encodeURIComponent(key)}/done`).then(r => r.data),
    {
      onSuccess: (res: any) => { refreshAll(); toast.success(res?.did || 'Done'); },
      onError: (err: any) => {
        // 409 means the item is real work that can't be waved away — say why.
        toast.error(err?.response?.data?.message || 'Could not update that');
      },
    }
  );

  const addTodo = useMutation(
    (text: string) => api.post('/attention/todo', { text }).then(r => r.data),
    {
      onSuccess: () => { setNewTodo(''); refreshAll(); },
      onError: () => { toast.error('Could not add that'); },
    }
  );

  function submitTodo(e: React.FormEvent) {
    e.preventDefault();
    const text = newTodo.trim();
    if (text) addTodo.mutate(text);
  }

  function isOpen(key: string) {
    return collapsed[key] !== undefined ? !collapsed[key] : OPEN_BY_DEFAULT.includes(key);
  }
  function toggle(key: string) {
    setCollapsed(prev => ({ ...prev, [key]: isOpen(key) }));
  }

  const allGroups = (data?.groups || []).filter(g => g.items.length > 0)
    .map(g => ({ ...g, items: g.items.filter(i => i.kind !== 'plan_buy') }))
    .filter(g => g.items.length > 0);
  // The dashboard / this page already has the big Friday buy card.
  const groups = compact ? allGroups.filter(g => g.key === 'now') : allGroups;
  // The owner's pending approvals count on the header with everything else: the live queue once it has loaded,
  // the server's own count until then (the list usually lands first).
  const pending = !isOwner ? 0 : approvals.data ? approvals.requests.length : (data?.pending_approvals ?? 0);
  const total = (data?.counts.total ?? 0) + pending;
  const urgent = (allGroups.find(g => g.key === 'now')?.items.length ?? 0) + pending;
  const rest = total - urgent;

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
        <AlertCircle className={`h-4 w-4 ${total > 0 ? 'text-red-500' : 'text-gray-300'}`} />
        <h2 className="text-base font-black uppercase tracking-tight text-gray-900">Needs Attention</h2>
        {(compact ? urgent : total) > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
            {compact ? urgent : total}
          </span>
        )}
        {data && data.counts.lead > 0 && (
          <span className="text-xs text-gray-500">
            {data.counts.lead} new {data.counts.lead === 1 ? 'lead' : 'leads'}
          </span>
        )}
        {compact && (
          <Link to="/attention" className="ml-auto text-sm text-brand-600 hover:text-brand-700">
            See all{rest > 0 ? ` (${rest} more)` : ''} →
          </Link>
        )}
      </div>

      {/* The approval queue, exactly as /approves draws it: a one-off's "Read first —" note always on top, then
          each step with No / Approve. Nothing below it has happened yet. #approvals scrolls here. */}
      {isOwner && approvals.requests.length > 0 && (
        <div id="approvals" className="border-b border-gray-200 bg-violet-50/40">
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <ShieldCheck className="h-4 w-4 text-violet-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Waiting on your OK</span>
            <span className="text-xs text-gray-500">{approvals.requests.length}</span>
            <span className="hidden sm:inline text-xs text-gray-500">nothing here has happened yet</span>
            <div className="ml-auto flex items-center gap-3">
              <ApproveAllButton queue={approvals} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-50" />
              <Link to="/approves" className="text-xs text-brand-600 hover:text-brand-700">Open Approves →</Link>
            </div>
          </div>
          <div className="px-4 pb-3">
            <ApprovalRows queue={approvals} />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="p-6 text-center text-gray-500">Loading…</div>
      ) : isError ? (
        <div className="p-6 text-center text-gray-500">Couldn't load the list.</div>
      ) : groups.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            {compact && rest > 0 ? 'Nothing urgent.' : pending > 0 ? 'Nothing else waiting on you.' : 'Nothing waiting on you.'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {compact && rest > 0
              ? `${rest} thing${rest === 1 ? '' : 's'} can wait — see all.`
              : 'Leads, notes, equipment and the schedule are all clear.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {groups.map(group => {
            const open = isOpen(group.key);
            return (
              <div key={group.key}>
                <button
                  onClick={() => toggle(group.key)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.label}
                  </span>
                  <span className="text-xs text-gray-500">{group.items.length}</span>
                  <ChevronDown
                    className={`ml-auto h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
                  />
                </button>

                {open && (
                  <ul className="divide-y divide-gray-50 border-t border-gray-100">
                    {group.items.map(item => (
                      <li key={item.key} id={item.key} className={`px-4 py-3 hover:bg-gray-50 ${wanted === item.key ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : ''}`}>
                        <div className="flex items-start gap-3">
                          {/* A first mow is one button; a review claim is money — approved or turned down, never ticked off. */}
                          {item.kind !== 'first_mow' && item.kind !== 'review' && (
                          <button
                            onClick={() => markDone.mutate(item.key)}
                            disabled={markDone.isLoading}
                            title="Check it off"
                            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full border-2 border-gray-300 text-transparent
                                       hover:border-green-500 hover:text-green-600 hover:bg-green-50
                                       flex items-center justify-center transition-colors disabled:opacity-40"
                          >
                            <Check className="h-3 w-3" />
                          </button>
                          )}

                          <div className="flex-1 min-w-0">
                            {/* The whole row is the link when there's somewhere to go —
                                tapping "Skipped Sat Aug 15 — Ali" should land on that job. */}
                            {item.href ? (
                              <Link to={item.href} className="block group">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-brand-700 group-hover:underline">
                                  {item.title}
                                  <AccountMark mark={item.account_mark} />
                                </p>
                                {item.detail && (
                                  <p className={detailClass(item.kind)}>{item.detail}</p>
                                )}
                              </Link>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-900">{item.title}<AccountMark mark={item.account_mark} /></p>
                                {item.detail && (
                                  <p className={detailClass(item.kind)}>{item.detail}</p>
                                )}
                              </>
                            )}

                            {/* The crew's winter-upsell photos: every one, thumb-sized, each opening full size. */}
                            {item.kind === 'interest' && !!item.photo_urls?.length && (
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {item.photo_urls.map((u, i) => (
                                  <a key={i} href={`${SERVER_ORIGIN}${u}`} target="_blank" rel="noreferrer"><img src={`${SERVER_ORIGIN}${u}`} alt="" className="h-12 w-12 rounded-lg object-cover border border-gray-200" /></a>
                                ))}
                              </div>
                            )}

                            {/* A Google review claim: the screenshot they sent, our review page, and the card's two buttons —
                                one filled, one outlined. Their labels say where the $10 goes, so they sit here where they fit. */}
                            {item.kind === 'review' && (
                              <div className="mt-1.5 space-y-1.5">
                                {(item.screenshot_url || item.review_url) && (
                                  <p className="text-xs flex flex-wrap gap-x-3">
                                    {item.screenshot_url && (
                                      <a href={`${SERVER_ORIGIN}${item.screenshot_url}`} target="_blank" rel="noreferrer" className="text-brand-700 underline">See their screenshot</a>
                                    )}
                                    {item.review_url && (
                                      <a href={item.review_url} target="_blank" rel="noreferrer" className="text-brand-700 underline">Our Google reviews</a>
                                    )}
                                  </p>
                                )}
                                {item.claim_id && (item.approve_label || item.reject_label) && (
                                  <div className="flex flex-wrap gap-2">
                                    {item.approve_label && (
                                      <button
                                        type="button"
                                        onClick={() => decideClaim.mutate({ id: Number(item.claim_id), action: 'approve' })}
                                        disabled={decideClaim.isLoading}
                                        title="The $10 stands — on their bill, or their next one"
                                        className="press px-2.5 py-1.5 rounded-xl text-[12px] font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50"
                                      >
                                        {item.approve_label}
                                      </button>
                                    )}
                                    {item.reject_label && (
                                      <button
                                        type="button"
                                        onClick={() => turnDownClaim(Number(item.claim_id))}
                                        disabled={decideClaim.isLoading}
                                        title="No $10 — it comes back off an unpaid bill, or stops waiting"
                                        className="px-2.5 py-1.5 rounded-xl text-[12px] font-semibold border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                      >
                                        {item.reject_label}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${kindColor[item.kind] || 'bg-gray-100 text-gray-600'}`}>
                                {kindLabel[item.kind] || item.kind}
                              </span>
                              {(item.why || []).map(w => (
                                <span key={w} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600">
                                  {w}
                                </span>
                              ))}
                              {item.when && <span className="text-[11px] text-gray-500">{ago(item.when)}</span>}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {item.phone && (
                              <a
                                href={`tel:${item.phone}`}
                                title={`Call ${item.who || item.phone}`}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50"
                              >
                                <Phone className="h-4 w-4" />
                              </a>
                            )}
                            {/* The thread in the app, from the business number — not the phone's own SMS app (owner, Oct 14 2026). */}
                            {item.phone && (
                              <Link
                                to={`/texts/${String(item.phone).replace(/\D/g, '').slice(-10)}`}
                                title={`Text ${item.who || item.phone} in the app`}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"
                              >
                                <MessageCircle className="h-4 w-4" />
                              </Link>
                            )}
                            {item.email && (
                              <a
                                href={`mailto:${item.email}`}
                                title={`Email ${item.who || item.email}`}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                              >
                                <Mail className="h-4 w-4" />
                              </a>
                            )}
                            {item.kind === 'offer' && (item as any).offer_id && (
                              <button
                                type="button"
                                onClick={() => skipOffer.mutate(Number((item as any).offer_id))}
                                disabled={skipOffer.isLoading}
                                title="Skip this one — nothing is booked. The tick puts them on the route."
                                className="px-2 py-1 rounded-lg text-[11px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
                              >
                                Skip
                              </button>
                            )}
                            {item.kind === 'care' && (
                              <button
                                type="button"
                                onClick={() => sendCrewBack(item.key)}
                                disabled={fixJob.isLoading}
                                title="Send the crew back to fix it — no charge, no crew pay"
                                className="px-2 py-1 rounded-lg text-[11px] font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50 whitespace-nowrap"
                              >
                                Send crew back
                              </button>
                            )}
                            {/* A text that asked for something (services/textAsks): the tap that finishes it, and the reply drafted on the thread. */}
                            {item.kind === 'ask' && item.subkind === 'quote' && (
                              <Link
                                to={item.customer_id ? `/calendar?quote=1&customer=${item.customer_id}` : '/calendar?quote=1'}
                                title="Put the quote visit on the calendar"
                                className="press px-2.5 py-1.5 rounded-xl text-[12px] font-semibold text-black bg-brand-600 hover:bg-brand-500 whitespace-nowrap"
                              >
                                Book visit
                              </Link>
                            )}
                            {item.kind === 'ask' && (
                              <Link
                                to={item.href || '/texts'}
                                title="Open the thread with a reply already written for you to edit"
                                className={`press px-2.5 py-1.5 rounded-xl text-[12px] font-semibold whitespace-nowrap ${item.subkind === 'quote' ? 'border border-gray-300 text-gray-800 bg-white' : 'text-black bg-brand-600 hover:bg-brand-500'}`}
                              >
                                Draft reply
                              </Link>
                            )}
                            {item.kind === 'first_mow' && (
                              <button
                                type="button"
                                onClick={() => markDone.mutate(item.key)}
                                disabled={markDone.isLoading}
                                title="Text them: how did the mow look, plus the Google review"
                                className="press px-2.5 py-1.5 rounded-xl text-[12px] font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50 whitespace-nowrap"
                              >
                                {item.action || 'Send check-ins'}
                              </button>
                            )}
                            {item.kind === 'autopay' && (item as any).invoice_id && (
                              <button
                                type="button"
                                onClick={() => chargeAgain.mutate((item as any).invoice_id)}
                                disabled={chargeAgain.isLoading}
                                title="Try the card on file again"
                                className="press px-2.5 py-1.5 rounded-xl text-[12px] font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50 whitespace-nowrap"
                              >
                                {chargeAgain.isLoading ? 'Charging…' : (item.action || 'Charge again')}
                              </button>
                            )}
                            {item.href && (
                              <Link
                                to={item.href}
                                title={item.action || 'Open'}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-brand-600 hover:bg-brand-50"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!compact && (
      <form onSubmit={submitTodo} className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
        <Plus className="h-4 w-4 text-gray-300 flex-shrink-0" />
        <input
          value={newTodo}
          onChange={e => setNewTodo(e.target.value)}
          placeholder="Add something to today…"
          className="flex-1 min-w-0 text-sm border-0 focus:ring-0 p-0 placeholder-gray-400"
        />
        {newTodo.trim() && (
          <button
            type="submit"
            disabled={addTodo.isLoading}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-40"
          >
            Add
          </button>
        )}
      </form>
      )}
    </div>
  );
}
